import {
  IPrequalificationFieldError,
  IPrequalificationInfoResponse,
  IPrequalifyData,
  PREQUAL_RENEWAL_PARAM,
  usePrequalificationInfo,
  usePrequalify,
} from "../../../services/prequalification";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApolloError } from "@apollo/client";
import { IPetItemResponse } from "../../../services/pet-list/pet-list.interface";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import { PREQUALIFICATION_ERROR_CODES } from "./constants";
import { PrequalAlreadyQualifiedScreen } from "./components/PrequalAlreadyQualifiedScreen";
import { PrequalCustomerInfoForm } from "./components/PrequalCustomerInfoForm";
import { PrequalErrorScreen } from "./components/PrequalErrorScreen";
import { PrequalPetPicker } from "./components/PrequalPetPicker";
import { PrequalProcessingScreen } from "./components/PrequalProcessingScreen";
import { PrequalResultScreen } from "./components/PrequalResultScreen";
import { isPrequalEligiblePet } from "../../../hooks/usePrequalFunnel";
import { getFormFieldErrors, hasEditableFieldError } from "./serverFieldErrors";
import { logPrequalEvent } from "./prequalAnalytics";
import { useCombinedAlertsDataStore }
  from "../../../hooks/redux-hooks/combined-alerts-data-store/combined-alerts-data-store";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { useUserStore } from "../../../hooks/redux-hooks/user-store/user.store";

type PrequalPhase = "form" | "processing" | "result" | "error";

// The backend rejects an invalid phone before calling Scratch, as a thrown
// GraphQL error (code in extensions.errorCode) rather than a field error.
const getInvalidPhoneError = (error: ApolloError): IPrequalificationFieldError | null => {
  const rejected = error.graphQLErrors?.find(
    (graphQLError) =>
      graphQLError.extensions?.errorCode === PREQUALIFICATION_ERROR_CODES.INVALID_PHONE,
  );
  return rejected ? { field: "phone", messages: [rejected.message] } : null;
};

const FullPageLoader = () => (
  <div className="flex h-full w-full flex-col items-center justify-center">
    <img src="/assets/icons/Foxhound_Loading.gif" className="w-96" alt="Loading" />
  </div>
);

const PrequalificationContainer = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { petList } = usePetStore();
  const { user } = useUserStore();
  const { endsSoonWPData } = useCombinedAlertsDataStore();

  const {
    fetchPrequalificationInfo,
    prequalificationInfo,
    loading: infoLoading,
    error: infoError,
  } = usePrequalificationInfo();
  const { prequalify } = usePrequalify();

  const [phase, setPhase] = useState<PrequalPhase>("form");
  const [mutationSettled, setMutationSettled] = useState(false);
  const [result, setResult] = useState<IPrequalificationInfoResponse | null>(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<IPrequalificationFieldError[] | null>(null);
  const [lastSubmitted, setLastSubmitted] = useState<IPrequalifyData | null>(null);
  // Same source the form uses to lock the email field as "On file".
  const emailOnFile = !!(prequalificationInfo?.email || user?.email);

  const petId = searchParams.get("id");
  // Renewal entry (from the interceptor modal), flagged by ?renewal=true. The
  // cohort itself comes from endsSoonWPData (the same source the interceptor
  // reads), so we never pass pet ids through the URL.
  const isRenewalEntry = searchParams.get(PREQUAL_RENEWAL_PARAM) === "true";
  const eligiblePets = useMemo(() => {
    if (isRenewalEntry) {
      // Renewal pets legitimately still hold their expiring plan, so
      // isPrequalEligiblePet (which excludes active plans) would drop them all.
      // Scope to the ends-soon cohort and gate only on species here.
      const renewalPetIds = new Set((endsSoonWPData ?? []).map((item) => item.petId));
      return (petList ?? []).filter(
        (petItem) =>
          renewalPetIds.has(petItem.id) &&
          (petItem.speciesName === "Dog" || petItem.speciesName === "Cat"),
      );
    }
    return (petList ?? []).filter(isPrequalEligiblePet);
  }, [petList, endsSoonWPData, isRenewalEntry]);
  // The pet the flow runs against: only a currently-eligible pet counts, so an
  // ineligible or unknown `?id=` is treated the same as a missing one.
  const pet = eligiblePets.find((petItem) => petItem.id === petId);
  // Deep link with an ambiguous target: no eligible pet resolved from the URL
  // and more than one to choose from. Rendered inline (see below) rather than
  // via a dashboard modal so we never navigate to show it.
  const needsPetSelection = !!petList && !pet && eligiblePets.length > 1;

  // Sets `?id=` while keeping the renewal flag, so a later re-render (or back
  // navigation) still scopes the funnel to the renewal cohort instead of
  // falling back to every eligible pet.
  const selectPetInUrl = useCallback(
    (selectedPetId: string) => {
      const nextParams: Record<string, string> = { id: selectedPetId };
      if (isRenewalEntry) nextParams[PREQUAL_RENEWAL_PARAM] = "true";
      setSearchParams(nextParams, { replace: true });
    },
    [setSearchParams, isRenewalEntry],
  );

  // Resolve a missing, invalid, or ineligible pet id (deep link): adopt the
  // single eligible pet, or return to the dashboard when there is nothing to
  // prequalify. The many-eligible-pets case falls through to the inline picker.
  useEffect(() => {
    if (!petList || pet) return;
    if (eligiblePets.length === 1) {
      selectPetInUrl(eligiblePets[0].id);
      return;
    }
    if (eligiblePets.length === 0) {
      navigate(PAGES_PATH.DASHBOARD, { replace: true });
    }
  }, [petList, pet, eligiblePets, navigate, selectPetInUrl]);

  useEffect(() => {
    if (needsPetSelection) {
      logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_PET_SELECT_SHOWN, {
        num_eligible_pets: eligiblePets.length,
      });
    }
  }, [needsPetSelection, eligiblePets.length]);

  const handlePetSelected = (selectedPet: IPetItemResponse) => {
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_PET_SELECTED, {
      pet_id: selectedPet.id,
      pet_name: selectedPet.name,
    });
    selectPetInUrl(selectedPet.id);
  };

  useEffect(() => {
    fetchPrequalificationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prequalificationInfo?.status === "ELIGIBLE" || prequalificationInfo?.status === "INELIGIBLE") {
      logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_STATUS_RECOGNIZED, {
        status: prequalificationInfo.status,
      });
    }
  }, [prequalificationInfo?.status]);

  const submitPrequalification = useCallback(
    async (data: IPrequalifyData) => {
      setLastSubmitted(data);
      setPhase("processing");
      setMutationSettled(false);
      setSubmitFailed(false);
      setResult(null);
      setFieldErrors(null);

      try {
        const response = await prequalify({ variables: { data } });
        setResult(response.data?.prequalify ?? null);
      } catch (error) {
        const phoneError =
          error instanceof ApolloError ? getInvalidPhoneError(error) : null;
        if (phoneError) {
          setFieldErrors([phoneError]);
          setPhase("form");
          return;
        }
        setSubmitFailed(true);
      }
      setMutationSettled(true);
    },
    [prequalify],
  );

  const handleFormSubmit = (formData: Omit<IPrequalifyData, "petId">) => {
    if (!petId) return;
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_ELIGIBILITY_CHECK_SUBMITTED, { pet_id: petId });
    // renewal drives backend Braze events; set explicitly (false for the
    // standard flow, true when entered via the renewal interceptor).
    submitPrequalification({ ...formData, petId, renewal: isRenewalEntry });
  };

  const handleProcessingFinished = useCallback(() => {
    const status = result?.status;
    if (!submitFailed && (status === "ELIGIBLE" || status === "INELIGIBLE")) {
      logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_ELIGIBILITY_RESULT, {
        status,
        monthly_available: status === "ELIGIBLE",
      });
      setPhase("result");
      return;
    }
    const errorReason = result?.errorReason ?? "request_failed";
    // Scratch rejected fields the customer can edit: back to the (rehydrated)
    // form so they can correct them, mirroring the tablet flow.
    const formFieldErrors =
      !submitFailed && status === "ERROR" ? getFormFieldErrors(result?.errors) : [];
    if (hasEditableFieldError(formFieldErrors, { emailOnFile })) {
      logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_ERROR_SHOWN, {
        error_reason: errorReason,
        destination: "form",
      });
      setFieldErrors(formFieldErrors);
      setPhase("form");
      return;
    }
    // Nothing editable to fix (timeout, 500, empty response, or only errors
    // on non-form data): generic error screen.
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_ERROR_SHOWN, {
      error_reason: errorReason,
      destination: "error_screen",
    });
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_SEE_PLANS, { result: "error" });
    setPhase("error");
  }, [result, submitFailed, emailOnFile]);

  const handleRetry = () => {
    if (lastSubmitted) {
      submitPrequalification(lastSubmitted);
    }
  };

  if (phase === "processing") {
    return (
      <PrequalProcessingScreen isComplete={mutationSettled} onFinished={handleProcessingFinished} />
    );
  }

  if (phase === "result" && result) {
    return (
      <PrequalResultScreen
        result={result}
        pet={petList?.find((petItem) => petItem.id === result.petId) ?? pet}
        userFirstName={user?.firstName}
        userEmail={user?.email}
      />
    );
  }

  if (phase === "error") {
    return <PrequalErrorScreen onRetry={handleRetry} />;
  }

  if (needsPetSelection) {
    return (
      <PrequalPetPicker
        pets={eligiblePets}
        locationName={user?.location?.locationName}
        onContinue={handlePetSelected}
      />
    );
  }

  // The gate query failed — without this branch the loader below would spin
  // forever, since prequalificationInfo never arrives.
  if (infoError && !prequalificationInfo && !infoLoading) {
    return <PrequalErrorScreen onRetry={() => fetchPrequalificationInfo()} />;
  }

  if (infoLoading || !prequalificationInfo || !pet) {
    return <FullPageLoader />;
  }

  if (prequalificationInfo.status === "ELIGIBLE" || prequalificationInfo.status === "INELIGIBLE") {
    return (
      <PrequalAlreadyQualifiedScreen
        record={prequalificationInfo}
        pet={petList?.find((petItem) => petItem.id === prequalificationInfo.petId) ?? pet}
        userFirstName={user?.firstName}
      />
    );
  }

  return (
    <PrequalCustomerInfoForm
      pet={pet}
      prefill={prequalificationInfo}
      user={user ?? undefined}
      initialValues={lastSubmitted}
      serverErrors={fieldErrors}
      onSubmit={handleFormSubmit}
    />
  );
};

export default PrequalificationContainer;
